/**
 * <igf-carousel> — arrow controls for a scroll-snap product track.
 *
 * The scrolling itself is native CSS; this only moves the track by one page
 * and keeps the arrows disabled at each end.
 */
class IgfCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.igf-carousel__track');
    this.prev = this.querySelector('[data-igf-prev]');
    this.next = this.querySelector('[data-igf-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => {
      this.stop();
      this.page(-1);
    });
    this.next?.addEventListener('click', () => {
      this.stop();
      this.page(1);
    });

    this.track.addEventListener('scroll', () => this.sync(), { passive: true });
    window.addEventListener('resize', () => this.sync(), { passive: true });

    this.interval = Number(this.dataset.autoplaySpeed) || 3000;
    this.autoplay = this.dataset.autoplay === 'true';

    // Hovering or tabbing in holds the carousel still so it cannot slide a
    // product out from under the pointer.
    ['pointerenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.pause()));
    ['pointerleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.resume()));

    this.onVisibility = () => (document.hidden ? this.pause() : this.resume());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.sync();
    this.resume();
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Advance one card, wrapping back to the start at the end. */
  tick() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (max <= 1) return;

    if (this.track.scrollLeft >= max - 1) this.track.scrollTo({ left: 0, behavior: 'smooth' });
    else this.page(1);
  }

  resume() {
    if (!this.autoplay || this.timer) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => this.tick(), this.interval);
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  /** Permanent stop once the visitor drives it themselves. */
  stop() {
    this.pause();
    this.autoplay = false;
  }

  /** Scroll by roughly one card, so a click always lands on a new item. */
  page(direction) {
    const card = this.track.querySelector('.igf-product');
    const step = card ? card.offsetWidth + 14 : this.track.clientWidth;
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  sync() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;

    // Nothing overflows, so there is nothing to page through: drop the
    // arrows rather than showing two permanently dead controls, and stop
    // autoplay ticking against a track that cannot move.
    const scrollable = max > 1;
    this.classList.toggle('is-static', !scrollable);
    if (!scrollable) this.pause();
    else this.resume();

    // 1px of slack, since scrollLeft can be fractional.
    if (this.prev) this.prev.disabled = x <= 1;
    if (this.next) this.next.disabled = x >= max - 1;
  }
}

customElements.define('igf-carousel', IgfCarousel);
